import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import {
  IconButton,
  Button,
  Grid,
  LinearProgress,
  makeStyles,
  InputBase,
  Typography,
  Box,
  Link,
  CircularProgress,
  Paper,
} from '@material-ui/core';
import {
  Search,
} from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import StepperBooking from './Layout/StepperBooking';
import { availabilitiesPeriod, maxWidth } from '../config/config';
import Map from './ScreenMap/Map';
import ClinicScheduler from './ScreenMap/ClinicScheduler';
import AppContext from '../state/AppContext';
import api from '../config/api';
import { fetchStaticToken } from '../helpers/authentication';
import MapAppointmentConfirmation from './ScreenMap/MapAppointmentConfirmation';
import filterSubservices from '../helpers/dataManipulations';
import clientCategoryState, { clientTypeState } from '../state';
import isLoadingClinicsState, {
  isLoadingAvailabilitiesState,
  isLocatingState,
} from '../state/apiRequestStatus';
import selectedClinicAvailabilitiesState, {
  preSelectedClinicIdState,
  selectedClinicAvailabilitiesFilteredState,
  selectedClinicAvailableDaysState,
  selectedClinicIdState,
  selectedSchedulerTherapistIdState,
  selectedServiceIdState,
  selectedSubserviceIdState,
  selectedTherapistState,
  selectedTimeSlotState,
  selectedTreatmentIdState,
} from '../state/selected';
import availableClinicIdsState, {
  clinicsState,
  servicesState,
  therapistsState,
} from '../state/apiData';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(11),
  },
  wrapperContent: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  inner: {
    maxWidth: maxWidth + theme.spacing(2),
  },
  colsWrapper: {
    marginTop: theme.spacing(6),
  },
  clinicList: {
    overflowY: 'auto',
    [theme.breakpoints.up('md')]: {
      maxHeight: 600,
    },
  },
  actionsWrapper: {
    display: 'flex',
    margin: 0,
  },
  form: {
    display: 'flex',
    margin: 0,
    alignItems: 'center',
    flex: '0 0 40%',
    padding: '0 0 0 7px',
    marginRight: theme.spacing(2),
  },
  postalCodeSearch: {
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(2),
  },
  postalCodeField: {
    flex: '1 0',
    '& > div': { backgroundColor: theme.palette.background.default },
  },
  postalCodeButton: {
    flex: '0 0',
  },
  locationButton: {
    flex: '1 0',
  },
  datetimeNotFound: {
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: '5px',
    '& p': {
      marginBottom: theme.spacing(0.5),
      marginTop: 0,
    },
    '& p:last-child': {
      marginBottom: 0,
    },
  },
}));

const ScreenMap = () => {
  const classes = useStyles();
  const intl = useIntl();

  const {
    mapAddress,
    setMapAddress,
    setIsUserSubmittingAddress,
    filteredMarkers,
    isUserSubmittingAddress,
    setSchedulerTherapists,
    appointmentToModify,
    setAppointmentToModify,
    setIsFollowUp,
    isFollowUp,
  } = useContext(AppContext);

  const setSelectedClinicAvailabilitiesFiltered = useSetRecoilState(
    selectedClinicAvailabilitiesFilteredState,
  );
  const [selectedClinicAvailabilities, setSelectedClinicAvailabilities] = useRecoilState(
    selectedClinicAvailabilitiesState,
  );
  const setIsLoadingAvailabilities = useSetRecoilState(isLoadingAvailabilitiesState);
  const setSelectedClinicAvailableDays = useSetRecoilState(selectedClinicAvailableDaysState);
  const setPreSelectedClinicId = useSetRecoilState(preSelectedClinicIdState);
  const therapists = useRecoilValue(therapistsState);
  const services = useRecoilValue(servicesState);
  const clientType = useRecoilValue(clientTypeState);
  const clinics = useRecoilValue(clinicsState);
  const clientCategory = useRecoilValue(clientCategoryState);
  const [availableClinicIds, setAvailableClinicIds] = useRecoilState(availableClinicIdsState);
  const [selectedTherapist, setSelectedTherapist] = useRecoilState(selectedTherapistState);
  const [isLoadingClinics, setIsLoadingClinics] = useRecoilState(isLoadingClinicsState);
  const [selectedClinicId, setSelectedClinicId] = useRecoilState(selectedClinicIdState);
  const [selectedTreatmentId, setSelectedTreatmentId] = useRecoilState(selectedTreatmentIdState);
  const [
    selectedSchedulerTherapistId, setSelectedSchedulerTherapistId,
  ] = useRecoilState(selectedSchedulerTherapistIdState);
  const [selectedServiceId, setSelectedServiceId] = useRecoilState(selectedServiceIdState);
  const [selectedSubserviceId, setSelectedSubserviceId] = useRecoilState(selectedSubserviceIdState);
  const [selectedTimeSlot, setSelectedTimeSlot] = useRecoilState(selectedTimeSlotState);
  const [isLocating, setIsLocating] = useRecoilState(isLocatingState);

  const [isLoadingAvailableClinicIds, setIsLoadingAvailableClinicIds] = useState(false);
  const [allAvailabilities, setAllAvailabilities] = useState([]);

  const containerElem = useRef(null);

  const today = dayjs();
  const end = today.add(availabilitiesPeriod.nb, availabilitiesPeriod.unit)
    .format('YYYY-MM-DD');

  const selectedService = services ? services.filter(
    (s) => s.externalTherapyID === selectedServiceId,
  )[0] : null;
  const selectedSubservice = selectedService && selectedService.subServices ? selectedService
    .subServices.filter((s) => s.client_type2Id === selectedSubserviceId)[0] : null;

  const fetchAvailableClinicIds = async () => {
    setIsLoadingAvailableClinicIds(true);
    setAvailableClinicIds([]);

    const responseAvailableClinicIds = await fetchStaticToken({
      url: api.availableClinicIds,
      method: 'POST',
      body: {
        disciplineID: selectedServiceId,
        subdisciplineID: selectedSubserviceId,
        treatmentID: selectedTreatmentId,
        client_type: clientType,
        client_category: clientCategory,
      },
    });

    if (responseAvailableClinicIds.status !== 200) {
      return setIsLoadingAvailableClinicIds(false);
    }

    const clinicIds = await responseAvailableClinicIds.json();
    setAvailableClinicIds(clinicIds);
    return setIsLoadingAvailableClinicIds(false);
  };

  const initTherapistsDropdown = (clinicAvailabilities) => {
    if (clinicAvailabilities) {
      const { schedulerTherapists } = clinicAvailabilities.reduce((acc, value) => {
        if (!acc.therapistsIds.includes(value.employeeID)) {
          const therapist = (therapists.filter((t) => (t.therapistId === value.employeeID))[0])
            || {
              firstName: '',
              therapistName: '',
            };
          acc.therapistsIds.push(value.employeeID);
          acc.schedulerTherapists.push({
            id: value.employeeID,
            name: `${therapist.firstName} ${therapist.therapistName}`,
          });
        }
        return acc;
      }, {
        schedulerTherapists: [],
        therapistsIds: [],
      });

      setSchedulerTherapists(schedulerTherapists);
      if (schedulerTherapists.length === 1) {
        setSelectedSchedulerTherapistId(schedulerTherapists[0].id);
      }
    }
  };

  const getAvailableDays = (availabilities) => availabilities.reduce((acc, av) => {
    if (!acc.includes(av.date)) {
      acc.push(av.date);
    }
    return acc;
  }, []);

  useEffect(async () => {
    if (containerElem && !selectedClinicId) {
      containerElem.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }

    if (selectedTherapist && selectedServiceId && selectedSubserviceId) {
      setIsLoadingClinics(true);
      setAllAvailabilities([]);
      setAvailableClinicIds([]);

      const responseAllAvailabilities = await fetchStaticToken({
        url: api.availabilities,
        method: 'POST',
        body: {
          therapistID: selectedTherapist.therapistId,
          disciplineID: selectedServiceId,
          subdisciplineID: selectedSubserviceId,
          treatmentID: selectedTreatmentId,
          client_type: clientType,
          client_category: clientCategory,
          dateRange:
            {
              start: today.format('YYYY-MM-DD'),
              end,
            },
        },
      });

      if (responseAllAvailabilities.status === 200) {
        const allAv = await responseAllAvailabilities.json();
        setAllAvailabilities(allAv);

        const clinicAvailabilities = allAv.filter(
          (av) => av.cliniqueID === selectedClinicId,
        );

        initTherapistsDropdown(clinicAvailabilities);
        setSelectedClinicAvailableDays(getAvailableDays(clinicAvailabilities));
        setSelectedClinicAvailabilities(clinicAvailabilities);
        setSelectedClinicAvailabilitiesFiltered(clinicAvailabilities);

        const clinicIds = allAv.reduce((acc, av) => {
          if (!acc.includes(av.cliniqueID)) {
            acc.push(av.cliniqueID);
          }
          return acc;
        }, []);

        setAvailableClinicIds(clinicIds);
      } else {
        setIsLoadingAvailableClinicIds(false);
      }

      return setIsLoadingClinics(false);
    }

    return fetchAvailableClinicIds();
  }, []);

  useEffect(async () => {
    if (selectedClinicId && selectedServiceId && selectedSubserviceId) {
      setIsLoadingAvailabilities(true);
      setSchedulerTherapists([]);
      setSelectedSchedulerTherapistId('');

      let clinicAvailabilities = [];
      if (selectedTherapist && selectedClinicId) {
        clinicAvailabilities = allAvailabilities.filter((av) => av.cliniqueID === selectedClinicId);
        setSelectedClinicAvailabilities(clinicAvailabilities);
      } else {
        const responseAvailabilities = await fetchStaticToken({
          url: api.availabilities,
          method: 'POST',
          body: {
            clinicID: selectedClinicId,
            disciplineID: selectedServiceId,
            subdisciplineID: selectedSubserviceId,
            treatmentID: selectedTreatmentId,
            client_type: clientType,
            client_category: clientCategory,
            dateRange:
              {
                start: today.format('YYYY-MM-DD'),
                end,
              },
          },
        });

        if (responseAvailabilities.status === 200 || responseAvailabilities.status === 201) {
          clinicAvailabilities = await responseAvailabilities.json();
        } else {
          clinicAvailabilities = [];
        }
      }

      initTherapistsDropdown(clinicAvailabilities);
      setSelectedClinicAvailableDays(getAvailableDays(clinicAvailabilities));
      setSelectedClinicAvailabilities(clinicAvailabilities);
      setSelectedClinicAvailabilitiesFiltered(clinicAvailabilities);
      setIsLoadingAvailabilities(false);
    }
  }, [selectedClinicId]);

  useEffect(async () => {
    if (selectedSchedulerTherapistId) {
      return setSelectedClinicAvailabilitiesFiltered(
        selectedClinicAvailabilities.filter(
          (av) => parseInt(av.employeeID, 10) === parseInt(selectedSchedulerTherapistId, 10),
        ),
      );
    }

    return setSelectedClinicAvailabilitiesFiltered(selectedClinicAvailabilities);
  }, [selectedSchedulerTherapistId]);

  const filteredSubservices = filterSubservices(
    selectedService.subServices, selectedTherapist, isFollowUp,
  );

  const renderUi = () => {
    const phoneLink = intl.formatMessage({ id: 'HeaderBar.telephoneNumberLink' });
    if (clinics && clinics.length > 0 && availableClinicIds && availableClinicIds.length > 0) {
      return (
        <Grid
          container
          spacing={2}
          justifyContent="center"
          className={classes.colsWrapper}
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <div
              className={classes.postalCodeSearch}
            >
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
              >
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <Typography component="p" variant="body2">
                    <FormattedMessage id="SelectAppointment.postalCodeLine1" />
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={9}
                >
                  <div className={classes.actionsWrapper}>
                    <Paper
                      component="form"
                      variant="outlined"
                      className={classes.form}
                      onSubmit={(e) => {
                        e.preventDefault();
                        setIsUserSubmittingAddress(true);
                      }}
                    >

                      <FormattedMessage id="SelectAppointment.postalCodeLabel">
                        {(msg) => (
                          <InputBase
                            name="address"
                            className={classes.postalCodeField}
                            placeholder={msg.toString()}
                            value={mapAddress}
                            onChange={(e) => {
                              setMapAddress(e.target.value);
                            }}
                          />
                        )}
                      </FormattedMessage>

                      <IconButton
                        className={classes.postalCodeButton}
                        type="submit"
                        size="small"
                        disabled={isUserSubmittingAddress}
                      >
                        {isUserSubmittingAddress
                          ? <CircularProgress size={20} />
                          : <Search />}
                      </IconButton>
                    </Paper>
                    <Button
                      className={classes.locationButton}
                      variant="contained"
                      type="submit"
                      size="small"
                      color="primary"
                      disabled={isLocating}
                      onClick={() => {
                        if (window && window.gtag) {
                          window.gtag('event', 'select_content', {
                            content_name: 'Use My Location',
                            content_lang: intl.locale,
                          });
                        }
                        setIsLocating(true);
                      }}
                    >
                      {isLocating
                        ? <CircularProgress size={20} />
                        : <FormattedMessage id="SelectAppointment.locationBtn" />}
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </div>
            <Map />
            <Box mt={2} mb={2} className={classes.datetimeNotFound}>
              <Typography variant="body1" align="center">
                <FormattedMessage
                  id="SelectAppointment.datetimeNotFound"
                  values={{
                    p: (...chunks) => <p>{chunks}</p>,
                    b: (...chunks) => <b>{chunks}</b>,
                    atel: (...chunks) => <a href={phoneLink}>{chunks}</a>,
                  }}
                />
              </Typography>
            </Box>
          </Grid>
          {selectedTimeSlot
            ? (
              <Grid
                item
                xs={12}
                md={6}
              >
                <MapAppointmentConfirmation />
              </Grid>
            )
            : (
              <Grid
                item
                xs={12}
                md={6}
              >
                <div className={classes.clinicList}>
                  {filteredMarkers.map(
                    (m) => <ClinicScheduler marker={m} key={m.clinic.id} />,
                  )}
                </div>
              </Grid>
            )}
        </Grid>
      );
    }
    return (
      <Alert severity="error">
        <FormattedMessage id="SelectAppointment.errorNoAvailableClinic" />
      </Alert>
    );
  };

  return (
    <div className={classes.container} ref={containerElem}>
      <StepperBooking activeStep={1} />
      <div className={classes.wrapperContent}>
        <Grid
          container
          spacing={2}
          justifyContent="center"
          style={{ minHeight: '100vh' }}
        >
          <Grid
            className={classes.inner}
            item
            xs={12}
          >
            <Box mb={3} mt={4}>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link
                component="button"
                onClick={() => {
                  if (window && window.gtag) {
                    window.gtag('event', 'select_content', {
                      content_name: 'Back to / Your availabilities',
                      content_lang: intl.locale,
                    });
                  }
                  setSelectedSubserviceId(null);
                  setSelectedTimeSlot(null);
                  setSelectedTreatmentId(1);
                  setSelectedClinicId(null);
                  setPreSelectedClinicId(null);
                  /* cases when we go on home screen */
                  if (filteredSubservices.length === 1 || isFollowUp
                    || appointmentToModify || availableClinicIds.length === 0) {
                    setIsFollowUp(false);
                    setSelectedServiceId(null);
                    setSelectedTherapist(null);
                    setAppointmentToModify(null);
                  }
                }}
              >
                <Typography variant="subtitle2" component="span">
                  <FormattedMessage id="SelectAppointment.previousStep" />
                </Typography>
              </Link>
            </Box>

            <Box mb={2}>
              <Typography variant="h3">
                {appointmentToModify
                  ? <FormattedMessage id="SelectAppointment.modifyAppointment" />
                  : <FormattedMessage id="SelectAppointment.selectDate" />}
                <strong>
                  {' '}
                  {selectedService.therapyName[intl.locale].toLowerCase()}
                  {selectedService.subServices.length > 1
                    && (` / ${selectedSubservice.subServiceName[intl.locale].toLowerCase()}`)}
                </strong>
                {selectedTherapist && (
                  <>
                    {' '}
                    <FormattedMessage id="SelectAppointment.with" />
                    {' '}
                    {selectedTherapist.firstName}
                    {' '}
                    {selectedTherapist.therapistName}
                  </>
                )}
              </Typography>
            </Box>
            <Box mb={4}>
              <Typography variant="body1">
                <FormattedMessage
                  id="SelectAppointment.subtitle"
                  values={{
                    b: (...chunks) => <b>{chunks}</b>,
                  }}
                />
              </Typography>
            </Box>

            {(isLoadingClinics || isLoadingAvailableClinicIds)
              ? <LinearProgress />
              : renderUi()}
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

ScreenMap.propTypes = {};

export default ScreenMap;
